import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Short explanation shown next to the label (for example why the option is disabled) */
  hint?: string;
}

interface RadioGroupProps {
  /** HTML name attribute shared by all radio inputs in the group */
  name: string;
  /** Available radio options */
  options: RadioOption[];
  /** Currently selected value */
  value?: string;
  /** Callback when selection changes */
  onChange?: (value: string) => void;
  /** Group label rendered as a fieldset legend */
  label?: string;
  /** Error message shown below the group */
  error?: string;
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  /** Additional CSS classes on the fieldset */
  className?: string;
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  label,
  error,
  direction = 'vertical',
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <legend className="text-sm font-medium text-text-secondary mb-1">
          {label}
        </legend>
      )}
      <div
        className={cn(
          'flex gap-3',
          direction === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        )}
        role="radiogroup"
      >
        {options.map((opt) => (
          <RadioItem
            key={opt.value}
            name={name}
            option={opt}
            checked={value === opt.value}
            onChange={onChange}
          />
        ))}
      </div>
      {error && (
        <p className="text-xs text-error-text" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

interface RadioItemProps {
  name: string;
  option: RadioOption;
  checked: boolean;
  onChange?: (value: string) => void;
}

const RadioItem = forwardRef<HTMLInputElement, RadioItemProps>(
  function RadioItem({ name, option, checked, onChange }, ref) {
    const inputId = `${name}-${option.value}`;

    return (
      <label
        htmlFor={inputId}
        title={option.hint}
        className={cn(
          'inline-flex items-center gap-2 select-none min-h-[var(--touch-target-min)] lg:min-h-0',
          option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <span className="relative flex items-center justify-center">
          <input
            ref={ref}
            id={inputId}
            type="radio"
            name={name}
            value={option.value}
            checked={checked}
            disabled={option.disabled}
            onChange={() => onChange?.(option.value)}
            className="peer sr-only"
          />
          <span
            className={cn(
              'flex items-center justify-center h-5 w-5 rounded-full border transition-all duration-150',
              // WCAG 1.4.11 (>= 3:1 against the surface): unchecked boundary on border-control, checked ring and dot
              // on accent-control (control-role tokens); border-focus is only the keyboard focus ring.
              'border-border-control bg-bg-secondary',
              'peer-checked:border-accent-control',
              'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-border-focus',
            )}
          >
            <span
              className={cn(
                'h-2.5 w-2.5 rounded-full bg-accent-control transition-transform duration-150',
                checked ? 'scale-100' : 'scale-0',
              )}
            />
          </span>
        </span>
        <span className="text-sm text-text-primary">{option.label}</span>
        {option.hint && <span className="text-xs text-text-tertiary">{option.hint}</span>}
      </label>
    );
  },
);

export default RadioGroup;
